import * as React from 'react';
import {
  useCreateProjectTeamAsync,
  useDeleteProjectTeamAsync,
  useGetProjectTeamsWithUserAsync,
  useUpdateProjectTeamAsync,
} from '@projectTeam/projectTeam.queries';
import {
  useCreateProjectCompanyAsync,
  useFetchCompanyOptions,
  useGetAllProjectCompaniesWithRolesAndUsers,
  useGetProjectCompanyAsync,
  useUpdateProjectCompanyAsync,
} from '@company/projectCompany/projectCompany.queries';
import { ComponentType, Icon, IOption, Select, Tooltip, useForm } from '@gcl/react-core';
import {
  IProjectCompany,
  IProjectCompanyOption,
  IUpdateProjectCompanyRequest,
} from '@company/projectCompany/projectCompany.models';
import { SearchPageBase } from '@common/SearchPageBase';
import { routes } from '@routes/routes.models';
import './UserList.scss';

import { IProjectTeam, IUpdateProjectTeamRequest } from '@projectTeam/projectTeam.models';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import {
  ICreateUserProfileRequest,
  IUpdateUserProfileRequest,
  IUserProfileBase,
} from '@userProfile/userProfile.models';
import { IUserInfo } from '@user/user.models';
import {
  useCreateProjectUserProfileAsync,
  useRemoveUserFromCompanyAsync,
  useUpdateProjectUserProfileAsync,
} from '@userProfile/projectUserProfile.queries';
import {
  IProjectUserProfile,
  IProjectUserProfileBase,
  IUpdateProjectUserProfileRequest,
} from '@userProfile/projectUserProfile.models';
import { useAppConfigs, useHasPermission, useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { EGeneralPermission, ERolePermission } from '@permission/permission.constants';
import { useNavigate } from '@routes/routes.hooks';
import { ButtonNavBar } from '@app/ButtonNavBar';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { useGetProjectRolesWithUser } from '@role/projectRole/projectRole.queries';
import { useTeamModal } from './TeamModal/teamModal.hooks';
import { useCompanyModal } from './CompanyModal/companyModal.hooks';
import { ETeamFormKeys } from './TeamModal/teamModal.models';
import { ECompanyFormKeys } from './CompanyModal/companyModal.models';
import { useMemberModal } from './MemberModal/memberModal.hooks';
import { EMemberFormKeys } from './MemberModal/memberModal.models';
import { OPTION_GLOBALE_PROJECT_USERS, OPTION_ALL_COMPANIES, OPTION_KEY_ALL_COMPANIES } from './userList.constants';
import { AllUsersList } from './AllUsersList';
import { useSelectedCompany } from './UserList.hooks';
import { UserTableContainer } from './UserTable/UserTableContainer';

export interface IUserListProps {}

export const UserList: React.FunctionComponent<IUserListProps> = ({}) => {
  const appConfigs = useAppConfigs();

  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();
  const isReadOnly = isProjectArchived;

  const hasUserSettingRight = useHasPermission(
    EGeneralPermission.USER_SETTING,
    ECompanyTypeIdConst.SYSTEM_USERS,
    EOrganizationTypeId.SYS,
  );
  const hasUserRightAdminRight = useHasPermission(
    ERolePermission.ADMINISTRATOR,
    ECompanyTypeIdConst.SYSTEM_USERS,
    EOrganizationTypeId.SYS,
  );

  const {
    selectedCompanyOption,
    setSelectedCompanyOption,
    selectedCompanyForForm,
    setSelectedCompanyForForm,
    isCompanyEditable,
  } = useSelectedCompany(isReadOnly);

  const [companyToUpdate, setCompanyToUpdate] = React.useState<IProjectCompany | undefined>(undefined);
  const [selectedMember, setSelectedMember] = React.useState<IProjectUserProfile | undefined>(undefined);
  const [selectedTeam, setSelectedTeam] = React.useState<IProjectTeam | undefined>(undefined);

  const { navigate, projectNo } = useNavigate();
  const queryClient = useQueryClient();

  const useTeamCreateUpdateForm = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const useCompanyCreateUpdateForm = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const useMemberCreateUpdateForm = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const { isDirty: isDirtyTeamForm } = useTeamCreateUpdateForm.formState;
  const { isDirty: isDirtyCompanyForm } = useCompanyCreateUpdateForm.formState;
  const { isDirty: isDirtyMemberForm } = useMemberCreateUpdateForm.formState;

  const {
    isLoading: isLoadingAllProjectCompaniesWithRolesAndUsers,
    isRefetching: isRefetchingAllProjectCompaniesWithRolesAndUsers,
  } = useGetAllProjectCompaniesWithRolesAndUsers({ projectId }, false);

  const {
    data: companyOptions,
    isLoading: isLoadingCompanyOptions,
    error: errorCompanyOptions,
  } = useFetchCompanyOptions({
    isDirty: isDirtyTeamForm,
  })({ projectId });

  const { data: company, error: errorCompany } = useGetProjectCompanyAsync(
    { projectId, id: selectedCompanyForForm?.data?.id || -1 },
    !isDirtyCompanyForm || !isDirtyMemberForm,
  );

  React.useEffect(() => {
    setCompanyToUpdate(company?.item);
  }, [JSON.stringify(company)]);

  React.useEffect(() => {
    if (!companyOptions || companyOptions.length <= 0) return;

    if (companyToUpdate && isPositiveNumber(companyToUpdate.id)) {
      const tempSelectedCompany = companyOptions.find((co) => co.data?.id === companyToUpdate.id);
      if (tempSelectedCompany) {
        setSelectedCompanyOption(companyOptions.find((co) => co.data?.id === companyToUpdate.id));
        queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY, companyToUpdate.id]);
        return;
      }
    }
    setSelectedCompanyOption(OPTION_ALL_COMPANIES);
  }, [JSON.stringify(companyOptions)]);

  const {
    data: teams,
    isLoading: isLoadingTeams,
    isRefetching: isRefetchingTeams,
    error: errorTeams,
  } = useGetProjectTeamsWithUserAsync(
    {
      projectId,
      companyId: selectedCompanyForForm?.data?.id || 0,
    },
    !isDirtyMemberForm,
  );

  const {
    data: roles,
    isLoading: isLoadingRoles,
    isRefetching: isRefetchingRoles,
    error: errorRoles,
  } = useGetProjectRolesWithUser(
    {
      projectId,
      companyId: selectedCompanyForForm?.data?.id || 0,
    },
    !isDirtyMemberForm,
  );

  const {
    mutate: mutateCreateProjectTeam,
    isLoading: isLoadingCreateProjectTeam,
    reset: resetCreateProjectTeam,
    isSuccess: isSuccessCreateProjectTeam,
    isError: isErrorCreateProjectTeam,
    error: errorCreateProjectTeam,
    variables: variablesCreateProjectTeam,
  } = useCreateProjectTeamAsync();

  const {
    mutate: mutateUpdateProjectTeam,
    isLoading: isLoadingUpdateProjectTeam,
    reset: resetUpdateProjectTeam,
    isSuccess: isSuccessUpdateProjectTeam,
    isError: isErrorUpdateProjectTeam,
    error: errorUpdateProjectTeam,
    variables: variablesUpdateProjectTeam,
  } = useUpdateProjectTeamAsync();

  const {
    mutate: mutateDeleteProjectTeam,
    isLoading: isLoadingDeleteProjectTeam,
    reset: resetDeleteProjectTeam,
    isSuccess: isSuccessDeleteProjectTeam,
    isError: isErrorDeleteProjectTeam,
    error: errorDeleteProjectTeam,
    variables: variablesDeleteProjectTeam,
  } = useDeleteProjectTeamAsync();

  React.useEffect(() => {
    if (isSuccessCreateProjectTeam || isSuccessUpdateProjectTeam) {
      closeTeamModal();
      queryClient.invalidateQueries([
        projectId,
        selectedCompanyForForm?.data?.id || 0,
        FETCH_QUERIES_NAME.TEAMS_WITH_USER,
      ]);

      if (
        variablesCreateProjectTeam?.assignMembers?.find(
          (member) => member.userProfileId === appConfigs.configs.myUserProfile?.id,
        ) ||
        variablesUpdateProjectTeam?.assignMembers?.find(
          (member) => member.userProfileId === appConfigs.configs.myUserProfile?.id,
        )
      ) {
        queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MY_DATA]);
      }
    }
  }, [isSuccessCreateProjectTeam, isSuccessUpdateProjectTeam]);

  React.useEffect(() => {
    if (isSuccessDeleteProjectTeam) {
      if (appConfigs.configs.myTeams?.find((team) => team.id === variablesDeleteProjectTeam?.id))
        queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MY_DATA]);
      resetDeleteProjectTeam();
      queryClient.invalidateQueries([
        projectId,
        selectedCompanyForForm?.data?.id || 0,
        FETCH_QUERIES_NAME.TEAMS_WITH_USER,
      ]);
    }
  }, [isSuccessDeleteProjectTeam]);

  const handleTeamFormData = (formData: any) => {
    return { ...formData, [ETeamFormKeys.COMPANY_ID]: formData[ETeamFormKeys.COMPANY].data.id, projectId };
  };

  const onCreateTeam = (formData: any) => {
    mutateCreateProjectTeam(handleTeamFormData(formData));
  };

  const onUpdateTeam = (updateTeam: IProjectTeam) => {
    return (formData: any) => {
      const updateTeamRequest = {
        ...handleTeamFormData(formData),
        id: updateTeam.id,
        rowVersion: updateTeam.rowVersion,
      } as IUpdateProjectTeamRequest;
      mutateUpdateProjectTeam(updateTeamRequest);
    };
  };

  const onDeleteTeam = (team: IProjectTeam) => {
    if (team.id) mutateDeleteProjectTeam({ id: team.id, projectId: team.projectId });
  };

  const { renderModal: renderConfirmDeleteTeamModal, openPortal: openConfirmDeleteTeamPortal } = useConfirmationModal({
    content: `Confirm to Delete team ${selectedTeam?.teamName}?`,
    onConfirm: () => {
      if (selectedTeam) onDeleteTeam(selectedTeam);
    },
  });

  const {
    renderModal: renderTeamModal,
    openPortal: openTeamModal,
    closePortal: closeTeamModal,
  } = useTeamModal({
    onConfirm: (updateTeam?: IProjectTeam) => {
      if (!updateTeam || !updateTeam.id || updateTeam.id <= 0) useTeamCreateUpdateForm.handleSubmit(onCreateTeam)();
      else useTeamCreateUpdateForm.handleSubmit(onUpdateTeam(updateTeam))();
    },
  });

  const onOpenTeamModal = (e: any) => {
    resetCreateProjectTeam();
    resetUpdateProjectTeam();

    useTeamCreateUpdateForm.reset();
    useTeamCreateUpdateForm.clearErrors();
    openTeamModal(e);
  };

  const {
    mutate: mutateCreateProjectCompany,
    isLoading: isLoadingCreateProjectCompany,
    data: dataCreateProjectCompany,
    reset: resetCreateProjectCompany,
    isSuccess: isSuccessCreateProjectCompany,
    isError: isErrorCreateProjectCompany,
    error: errorCreateProjectCompany,
  } = useCreateProjectCompanyAsync();

  const {
    mutate: mutateUpdateProjectCompany,
    isLoading: isLoadingUpdateProjectCompany,
    reset: resetUpdateProjectCompany,
    isSuccess: isSuccessUpdateProjectCompany,
    isError: isErrorUpdateProjectCompany,
    error: errorUpdateProjectCompany,
  } = useUpdateProjectCompanyAsync();

  React.useEffect(() => {
    if (!isSuccessCreateProjectCompany && !isSuccessUpdateProjectCompany) return;

    closeCompanyModal();
    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.COMPANY_OPTIONS]);
    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY_ALL_WITH_ROLES_AND_USERS]);

    if (isSuccessCreateProjectCompany) {
      setCompanyToUpdate(dataCreateProjectCompany.item);
    }
  }, [isSuccessCreateProjectCompany, isSuccessUpdateProjectCompany]);

  const handleCompanyFormData = (formData: any) => {
    return {
      ...formData,
      projectId,
      [ECompanyFormKeys.MAX_RISK_CATEGORY]: formData[ECompanyFormKeys.MAX_RISK_CATEGORY].data,
      [ECompanyFormKeys.TYPE_ID]: formData[ECompanyFormKeys.TYPE].data.id,
      [ECompanyFormKeys.ORGANIZATION_TYPE_ID]: formData[ECompanyFormKeys.ORGANIZATION_TYPE][0].data.id,
    };
  };

  const onCreateCompany = (formData: any) => {
    mutateCreateProjectCompany(handleCompanyFormData(formData));
  };

  const onUpdateCompany = (id: number, rowVersion: string) => {
    return (formData: any) => {
      const updateCompanyRequest = {
        ...handleCompanyFormData(formData),
        id,
        rowVersion,
      } as IUpdateProjectCompanyRequest;
      mutateUpdateProjectCompany(updateCompanyRequest);
    };
  };

  const {
    renderModal: renderCompanyModal,
    openPortal: openCompanyModal,
    closePortal: closeCompanyModal,
  } = useCompanyModal({
    onConfirm: (updateCompany?: IProjectCompany) => {
      useCompanyCreateUpdateForm.clearErrors();
      if (!updateCompany || !updateCompany.id || updateCompany.id <= 0)
        useCompanyCreateUpdateForm.handleSubmit(onCreateCompany)();
      else {
        const updateCompanyFn = onUpdateCompany(updateCompany.id, updateCompany.rowVersion ?? '');
        useCompanyCreateUpdateForm.handleSubmit(updateCompanyFn)();
      }
    },
    projectId,
  });

  const onOpenCompanyModal = (e: any) => {
    resetCreateProjectCompany();
    resetUpdateProjectCompany();

    useCompanyCreateUpdateForm.reset();
    useCompanyCreateUpdateForm.clearErrors();
    openCompanyModal(e);
  };

  const {
    mutate: mutateCreateProjectMember,
    isLoading: isLoadingCreateProjectMember,
    data: dataCreateProjectMember,
    reset: resetCreateProjectMember,
    isSuccess: isSuccessCreateProjectMember,
    isError: isErrorCreateProjectMember,
    error: errorCreateProjectMember,
  } = useCreateProjectUserProfileAsync();

  const {
    mutate: mutateUpdateProjectMember,
    isLoading: isLoadingUpdateProjectMember,
    reset: resetUpdateProjectMember,
    isSuccess: isSuccessUpdateProjectMember,
    isError: isErrorUpdateProjectMember,
    error: errorUpdateProjectMember,
    data: dataUpdateProjectMember,
  } = useUpdateProjectUserProfileAsync();

  const {
    mutate: mutateRemoveUserFromCompany,
    isLoading: isLoadingRemoveUserFromCompany,
    reset: resetRemoveUserFromCompany,
    isSuccess: isSuccessRemoveUserFromCompany,
    isError: isErrorRemoveUserFromCompany,
    error: errorRemoveUserFromCompany,
    variables: variablesRemoveUserFromCompany,
  } = useRemoveUserFromCompanyAsync();

  const onRemoveUserFromCompany = (companyId: number | undefined, projectUserProfileId: number | undefined) => {
    if (!isPositiveNumber(companyId) || !isPositiveNumber(projectUserProfileId)) return;

    mutateRemoveUserFromCompany({
      projectId,
      companyId: companyId ?? -1,
      projectUserProfileId: projectUserProfileId ?? -1,
    });
  };

  const reloadUserTableData = () => {
    queryClient.invalidateQueries([
      projectId,
      selectedCompanyForForm?.data?.id || 0,
      FETCH_QUERIES_NAME.TEAMS_WITH_USER,
    ]);
    queryClient.invalidateQueries([
      projectId,
      selectedCompanyForForm?.data?.id || 0,
      FETCH_QUERIES_NAME.ROLES_WITH_USER,
    ]);
    queryClient.invalidateQueries([projectId, companyToUpdate?.companyTypeId || 0, FETCH_QUERIES_NAME.ROLE_OPTIONS]);
  };

  React.useEffect(() => {
    if (!isSuccessCreateProjectMember && !isSuccessUpdateProjectMember) return;
    reloadUserTableData();
    closeMemberModal();
    if (
      dataUpdateProjectMember?.item?.userProfileId === appConfigs.configs.myProjectUserProfile?.userProfileId ||
      dataCreateProjectMember?.item?.userProfileId === appConfigs.configs.myProjectUserProfile?.userProfileId
    ) {
      queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MY_DATA]);
    }
    resetCreateProjectMember();
    resetUpdateProjectMember();
    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY_ALL_WITH_ROLES_AND_USERS]);
  }, [isSuccessCreateProjectMember, isSuccessUpdateProjectMember]);

  React.useEffect(() => {
    if (!isSuccessRemoveUserFromCompany) return;
    reloadUserTableData();
    closeMemberModal();
    if (variablesRemoveUserFromCompany?.projectUserProfileId === appConfigs.configs.myProjectUserProfile?.id) {
      queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MY_DATA]);
    }
    resetRemoveUserFromCompany();
    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY_ALL_WITH_ROLES_AND_USERS]);
  }, [isSuccessRemoveUserFromCompany]);

  const handleMemberFormData = (formData: any, updateMember?: IProjectUserProfile) => {
    const formUserProfile =
      updateMember?.userProfile || (formData[EMemberFormKeys.USER_PROFILE] as IUserProfileBase | undefined);
    const formProjectUserProfile =
      updateMember || (formData[EMemberFormKeys.PROJECT_USER_PROFILE] as IProjectUserProfileBase | undefined);
    const formUserInfo = formData[EMemberFormKeys.USER_INFO] as IUserInfo | undefined;

    let userProfile: ICreateUserProfileRequest | IUpdateUserProfileRequest = {
      uid: formUserProfile?.uid || formUserInfo?.id,
      email: formUserProfile?.email || formUserInfo?.mail,
      upn: formUserProfile?.upn || formUserInfo?.userPrincipalName,
      firstName: formUserProfile?.firstName || formUserInfo?.surname || formUserInfo?.displayName,
      lastName: formUserProfile?.lastName || formUserInfo?.givenName,
      title: formUserProfile?.title,
      tel: formData[EMemberFormKeys.TEL],
      fax: undefined,
      lastProjectId: undefined,
      lastLoginDate: undefined,
      isLocked: false,
      defaultRightPanelClosed: false,
      loginTime: undefined,
      twapprovalType: undefined,
      qualification: undefined,
      maxTeamBoardMasterId: undefined,
      gemsLead: undefined,
      ekpid: undefined,
      employeeId: undefined,
      isExternal: formData[EMemberFormKeys.IS_EXTERNAL],
    } as ICreateUserProfileRequest;

    if (formUserProfile && formUserProfile.id && formUserProfile.id > 0) {
      userProfile = {
        ...userProfile,
        id: formUserProfile.id,
        rowVersion: formUserProfile.rowVersion,
      } as IUpdateUserProfileRequest;
    }

    return {
      projectId,
      id: formProjectUserProfile?.id || 0,
      company: selectedCompanyForForm?.data,
      teams: formData[EMemberFormKeys.TEAMS]?.map((team: any) => {
        const { users, ...teamBase } = team;
        return teamBase;
      }),
      roles: formData[EMemberFormKeys.ROLES]?.map((r: any) => {
        const { users, role, companyType, ...roleBase } = r;
        return roleBase;
      }),
      userProfile,
    };
  };

  const onCreateUpdateMember = (updateMember?: IProjectUserProfile) => {
    return (formData: any) => {
      const request = handleMemberFormData(formData, updateMember);
      const isCreate = !(request.userProfile as any).id || (request.userProfile as any).id <= 0;

      if (isCreate) mutateCreateProjectMember(request);
      else mutateUpdateProjectMember(request as IUpdateProjectUserProfileRequest);
    };
  };

  const {
    renderModal: renderMemberModal,
    openPortal: openMemberModal,
    closePortal: closeMemberModal,
  } = useMemberModal({
    onConfirm: (updateMember?: IProjectUserProfile) => {
      useMemberCreateUpdateForm.handleSubmit(onCreateUpdateMember(updateMember))();
    },
    shouldIncludeHiddenRoles: true,
  });

  const onOpenMemberModal = (e: any, projectUserProfile?: IProjectUserProfile) => {
    setSelectedMember(projectUserProfile);
    resetCreateProjectMember();
    resetUpdateProjectMember();

    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.SEARCH_USER_INFO]);
    useMemberCreateUpdateForm.reset();
    useMemberCreateUpdateForm.clearErrors();
    openMemberModal(e);
  };

  return (
    <>
      <ApiErrorDisplay
        errors={[
          { error: errorCompanyOptions, genericError: 'Get company options failed' },
          { error: errorCompany, genericError: 'Get company failed' },
          { error: errorTeams, genericError: 'Get teams failed' },
          { error: errorDeleteProjectTeam, genericError: 'Delete team failed', reset: resetDeleteProjectTeam },
        ]}
      />
      <SearchPageBase
        data={companyOptions}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            prefixIconName: 'People',
            text: 'Users',
            active: true,
          },
        ]}
        title="Authorised Users"
        isTableLoading={isLoadingCompanyOptions || isLoadingTeams || isLoadingAllProjectCompaniesWithRolesAndUsers}
        isTableRefetching={isRefetchingTeams || isRefetchingAllProjectCompaniesWithRolesAndUsers}
        shouldUpdateArrow={false}
        onRenderTableHeaderComponent={() => {
          return (
            <div className="gcl-gems-user-list-table-header">
              <ButtonNavBar
                items={[
                  {
                    text: 'TWC Applications',
                    onClick: () => navigate(routes.users.twcRequest.list.path),
                    componentType: ComponentType.Secondary,
                  },
                  {
                    text: 'GE Applications',
                    onClick: () => navigate(routes.users.geRequest.list.path),
                    componentType: ComponentType.Secondary,
                  },
                ]}
                className="gcl-pb-sm"
              />
              <div className="gcl-gems-user-list-table-header-label">
                <LabelWithMessage labelFor="gems_user_list_company_select" text="Company" />
                {hasUserSettingRight && !isReadOnly ? (
                  <Tooltip overlay="Add Company" id="gcl-gems-user-table-add-company-btn-tooltip">
                    <Icon
                      data-testid="add-Company"
                      iconName="PlusLg"
                      className="gcl-gems-user-list-company-icon"
                      onClick={(e) => {
                        setCompanyToUpdate(undefined);
                        onOpenCompanyModal(e);
                      }}
                    />
                  </Tooltip>
                ) : undefined}
              </div>
              <div className="gcl-gems-user-list-table-header-company-select">
                <Select
                  disabled={false}
                  id="gems_user_list_company_select"
                  className="gcl-gems-user-list-company-select"
                  onChange={(newValue: IOption<IProjectCompanyOption>[]) => {
                    if (newValue && newValue.length > 0) setSelectedCompanyOption(newValue[0]);
                    else setSelectedCompanyOption(undefined);
                  }}
                  value={selectedCompanyOption ? [selectedCompanyOption] : undefined}
                  options={[OPTION_ALL_COMPANIES, ...(companyOptions || []), OPTION_GLOBALE_PROJECT_USERS]}
                  allowSearch={false}
                  clearable={false}
                />

                {hasUserSettingRight && isCompanyEditable ? (
                  <>
                    <Tooltip
                      overlay={`Edit ${selectedCompanyOption?.data?.name}`}
                      id="gcl-gems-user-table-edit-company-btn-tooltip"
                    >
                      <Icon
                        data-testid="update-Company"
                        iconName="Pencil"
                        className="gcl-gems-user-list-company-icon"
                        onClick={(e) => {
                          setCompanyToUpdate(company?.item || undefined);
                          onOpenCompanyModal(e);
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      overlay={`Add ${selectedCompanyOption?.data?.name} Member`}
                      id="gcl-gems-user-table-add-company-member-btn-tooltip"
                    >
                      <Icon
                        data-testid="add-User"
                        iconName="PersonPlus"
                        className="gcl-gems-user-list-company-icon"
                        onClick={(e) => {
                          setSelectedMember(undefined);
                          onOpenMemberModal(e);
                        }}
                      />
                    </Tooltip>
                  </>
                ) : undefined}
              </div>
            </div>
          );
        }}
        onRenderTable={() => {
          if (selectedCompanyOption?.key === OPTION_KEY_ALL_COMPANIES) {
            return (
              <AllUsersList
                setSelectedMember={setSelectedMember}
                onOpenCompanyModal={onOpenCompanyModal}
                onOpenMemberModal={onOpenMemberModal}
                setSelectedCompanyForForm={setSelectedCompanyForForm}
                companyOptions={companyOptions}
                isReadOnly={isReadOnly}
                hasUserSettingRight={hasUserSettingRight}
                openConfirmDeleteTeamPortal={openConfirmDeleteTeamPortal}
                openMemberModal={onOpenMemberModal}
                openTeamModal={onOpenTeamModal}
                isDirtyMemberForm={isDirtyMemberForm}
                hasUserRightAdminRight={hasUserRightAdminRight}
                setSelectedTeam={setSelectedTeam}
              />
            );
          }
          return (
            <>
              <UserTableContainer
                teamDisplayMode="company"
                hasEditRight={hasUserRightAdminRight && isCompanyEditable}
                isDirtyMemberForm={isDirtyMemberForm}
                openTeamModal={onOpenTeamModal}
                openMemberModal={onOpenMemberModal}
                openConfirmDeleteTeamPortal={openConfirmDeleteTeamPortal}
                setSelectedTeam={setSelectedTeam}
                companyOption={selectedCompanyOption}
              />
            </>
          );
        }}
        legend={
          <>
            <ul>
              <li>
                <span className="gcl-gems-user-list-legend enroll">Pending</span> - This person has NOT complete the
                Temporary Works e-learning. Please enroll in Workday.
              </li>
              <li>
                <span className="gcl-gems-user-list-legend in-progress">In Progress</span> - This person has not
                completed the Temporary Works e-learning.
              </li>
              <li>
                <span className="gcl-gems-user-list-legend completed">Completed</span> - This person has completed the
                Temporary Works e-learning.
              </li>
              <li>
                <span className="gcl-gems-user-list-legend locked">Locked</span> - This person has been locked from
                using GEMS.
              </li>
            </ul>
            <div className="gcl-fw-600">
              * The e-learning enrollment and completion status is updated daily overnight.
            </div>
          </>
        }
      />
      {renderCompanyModal({
        company: companyToUpdate,
        isSubmitLoading: isLoadingCreateProjectCompany || isLoadingUpdateProjectCompany,
        isSubmitError: isErrorCreateProjectCompany || isErrorUpdateProjectCompany,
        isSubmitSuccess: isSuccessCreateProjectCompany || isSuccessUpdateProjectCompany,
        useFormMethods: useCompanyCreateUpdateForm,
        errorUpdateProjectCompany,
        errorCreateProjectCompany,
      })}
      {renderMemberModal({
        isSubmitLoading: isLoadingCreateProjectMember || isLoadingUpdateProjectMember,
        isSubmitError: isErrorCreateProjectMember || isErrorUpdateProjectMember,
        isSubmitSuccess: isSuccessCreateProjectMember || isSuccessUpdateProjectMember,
        onUserLockUnlockSuccess: () => {
          reloadUserTableData();
          closeMemberModal();
        },

        projectId,
        projectUserProfile: selectedMember,
        company: company?.item,
        teams,
        rolesWithUser: roles,
        useFormMethods: useMemberCreateUpdateForm,
        errorUpdateProjectUserProfile: errorUpdateProjectMember,
        errorCreateProjectUserProfile: errorCreateProjectMember,
        removeUserFromCompany: onRemoveUserFromCompany,
        isRemoveFromCompanyLoading: isLoadingRemoveUserFromCompany,
        isRemoveFromCompanyError: isErrorRemoveUserFromCompany,
        isRemoveFromCompanySuccess: isSuccessRemoveUserFromCompany,
        isShowTeams: true,
        selectedCompanyId: selectedCompanyForForm?.data?.id,
        isShowSyncTraining: true,
      })}
      {renderTeamModal({
        shouldDisplay: selectedCompanyForForm?.key !== 0,
        isSubmitLoading: isLoadingCreateProjectTeam || isLoadingUpdateProjectTeam,
        isSubmitError: isErrorCreateProjectTeam || isErrorUpdateProjectTeam,
        isSubmitSuccess: isSuccessCreateProjectTeam || isSuccessUpdateProjectTeam,
        errorCreateProjectTeam,
        errorUpdateProjectTeam,
        projectId,
        team: selectedTeam,
        companyOptions,
        defaultCompany: selectedCompanyForForm,
        useFormMethods: useTeamCreateUpdateForm,
      })}
      {renderConfirmDeleteTeamModal(isLoadingDeleteProjectTeam, isErrorDeleteProjectTeam, isSuccessDeleteProjectTeam)}
    </>
  );
};
